/**
 * FilterBar Component
 * 
 * A highly dynamic and beautiful filter component for data tables.
 * Supports multiple filter types with smooth animations and excellent UX.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ChevronDown, 
  Check, 
  RotateCcw,
  SlidersHorizontal,
} from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export type FilterType = 'select' | 'multi-select' | 'toggle';

export interface FilterOption {
  value: string | number | boolean;
  label: string;
  icon?: React.ReactNode;
  color?: string;
  count?: number;
}

export interface FilterConfig {
  id: string;
  label: string;
  type: FilterType;
  options: FilterOption[];
  defaultValue?: string | number | boolean | (string | number)[];
  placeholder?: string;
  icon?: React.ReactNode;
}

export interface FilterValues {
  [key: string]: string | number | boolean | (string | number)[] | undefined;
}

export interface FilterBarProps {
  filters: FilterConfig[];
  values: FilterValues;
  onChange: (values: FilterValues) => void;
  totalItems?: number;
  filteredItems?: number;
  className?: string;
  /** Extra filter controls rendered inline alongside the filter chips */
  extraFilters?: React.ReactNode;
  /** Replaces the default multi-select clear. Use this to also reset search, dates, and sort. */
  onClearAll?: () => void;
}

// =============================================================================
// FILTER CHIP COMPONENT
// =============================================================================

interface FilterChipProps {
  config: FilterConfig;
  value: string | number | boolean | (string | number)[] | undefined;
  onChange: (value: unknown) => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const FilterChip: React.FC<FilterChipProps> = ({
  config,
  value,
  onChange,
  isOpen,
  onToggle,
  onClose,
}) => {
  const [optionQuery, setOptionQuery] = useState('');
  useEffect(() => {
    if (!isOpen) setOptionQuery('');
  }, [isOpen]);

  const visibleOptions = useMemo(() => {
    const query = optionQuery.trim().toLowerCase();
    if (!query || config.type === 'toggle') return config.options;
    return config.options.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [config.options, config.type, optionQuery]);

  const hasValue = useMemo(() => {
    if (value === undefined || value === '' || value === 'all') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  }, [value]);

  const displayValue = useMemo(() => {
    if (!hasValue) return config.placeholder || 'All';
    
    if (config.type === 'toggle') {
      return value ? 'Yes' : 'No';
    }
    
    if (config.type === 'multi-select' && Array.isArray(value)) {
      if (value.length === 1) {
        const option = config.options.find(o => o.value === value[0]);
        return option?.label || value[0];
      }
      return `${value.length} selected`;
    }
    
    const option = config.options.find(o => o.value === value);
    return option?.label || value;
  }, [value, hasValue, config]);

  const selectedOption = useMemo(() => {
    if (config.type === 'multi-select') return null;
    return config.options.find(o => o.value === value);
  }, [value, config]);

  return (
    <div className="relative">
      <motion.button
        onClick={onToggle}
        className={`
          inline-flex items-center gap-2
          px-3 py-2 rounded-xl
          text-sm font-medium
          border-2 transition-all duration-200
          ${hasValue
            ? 'bg-primary-500/10 border-primary-500/30 text-primary-500'
            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
          }
          ${isOpen ? 'ring-2 ring-primary-500/20' : ''}
        `}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {config.icon && (
          <span className={hasValue ? 'text-primary-500' : 'text-gray-400'}>
            {config.icon}
          </span>
        )}
        <span className="max-w-[120px] truncate">{config.label}</span>
        {hasValue && (
          <>
            <span className="text-gray-300">|</span>
            <span className="font-semibold flex items-center gap-1">
              {selectedOption?.icon}
              {displayValue}
            </span>
          </>
        )}
        <ChevronDown 
          size={14} 
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
        {hasValue && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            onClick={(e) => {
              e.stopPropagation();
              onChange(config.type === 'multi-select' ? [] : 'all');
            }}
            className="
              ml-1 p-0.5 rounded-full
              hover:bg-primary-500/20
              transition-colors
            "
          >
            <X size={12} />
          </motion.button>
        )}
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-[999]" 
              onClick={onClose}
            />
            
            {/* Dropdown Menu */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="
                absolute top-full left-0 mt-2
                min-w-[200px] max-w-[280px]
                bg-white rounded-xl
                border border-gray-200
                shadow-xl shadow-gray-200/50
                z-[1000] overflow-hidden
              "
            >
              {/* Header */}
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {config.label}
                </p>
                {config.type !== 'toggle' && (
                  <input
                    type="search"
                    value={optionQuery}
                    onChange={(event) => setOptionQuery(event.target.value)}
                    placeholder="Search"
                    aria-label={`Search ${config.label}`}
                    className="mt-2 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-800 outline-none focus:border-gray-400"
                  />
                )}
              </div>

              {/* Options */}
              <div className="p-1.5 max-h-[240px] overflow-y-auto">
                {config.type !== 'multi-select' && optionQuery.trim() === '' && (
                  <DropdownOption
                    label="All"
                    isSelected={!hasValue}
                    onClick={() => {
                      onChange('all');
                      onClose();
                    }}
                  />
                )}
                
                {visibleOptions.length === 0 && (
                  <p className="px-3 py-2 text-sm text-gray-400">No matches</p>
                )}

                {visibleOptions.map((option) => {
                  const isSelected = config.type === 'multi-select'
                    ? Array.isArray(value) && value.includes(option.value as string | number)
                    : value === option.value;

                  return (
                    <DropdownOption
                      key={String(option.value)}
                      label={option.label}
                      icon={option.icon}
                      color={option.color}
                      count={option.count}
                      isSelected={isSelected}
                      onClick={() => {
                        if (config.type === 'multi-select') {
                          const currentValues = Array.isArray(value) ? value : [];
                          const newValues = isSelected
                            ? currentValues.filter(v => v !== option.value)
                            : [...currentValues, option.value as string | number];
                          onChange(newValues);
                        } else {
                          onChange(option.value);
                          onClose();
                        }
                      }}
                    />
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// =============================================================================
// DROPDOWN OPTION
// =============================================================================

interface DropdownOptionProps {
  label: string;
  icon?: React.ReactNode;
  color?: string;
  count?: number;
  isSelected: boolean;
  onClick: () => void;
}

const DropdownOption: React.FC<DropdownOptionProps> = ({
  label,
  icon,
  color,
  count,
  isSelected,
  onClick,
}) => (
  <button
    onClick={onClick}
    className={`
      w-full flex items-center gap-2
      px-3 py-2 rounded-lg
      text-sm text-left
      transition-all duration-150
      ${isSelected
        ? 'bg-primary-500/10 text-primary-500'
        : 'text-gray-700 hover:bg-gray-100'
      }
    `}
  >
    {/* Checkbox/Radio indicator */}
    <div className={`
      w-4 h-4 rounded-md flex items-center justify-center flex-shrink-0
      border-2 transition-colors
      ${isSelected
        ? 'bg-primary-500 border-primary-500'
        : 'border-gray-300'
      }
    `}>
      {isSelected && <Check size={10} className="text-white" />}
    </div>

    {/* Icon */}
    {icon && (
      <span style={{ color: color || 'inherit' }}>
        {icon}
      </span>
    )}

    {/* Label */}
    <span className="flex-1 truncate font-medium">{label}</span>

    {/* Count badge */}
    {count !== undefined && (
      <span className={`
        px-1.5 py-0.5 rounded-full text-[10px] font-semibold
        ${isSelected ? 'bg-primary-500/20' : 'bg-gray-100 text-gray-500'}
      `}>
        {count}
      </span>
    )}
  </button>
);

// =============================================================================
// MAIN FILTER BAR COMPONENT
// =============================================================================

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  values,
  onChange,
  totalItems,
  filteredItems,
  className = '',
  extraFilters,
  onClearAll,
}) => {
  const [openFilter, setOpenFilter] = useState<string | null>(null);

  const activeFiltersCount = useMemo(() => {
    return Object.entries(values).filter(([, val]) => {
      if (val === undefined || val === '' || val === 'all') return false;
      if (Array.isArray(val)) return val.length > 0;
      return true;
    }).length;
  }, [values]);

  const handleFilterChange = useCallback((filterId: string, value: unknown) => {
    onChange({ ...values, [filterId]: value as FilterValues[string] });
  }, [values, onChange]);

  const handleClearAll = useCallback(() => {
    const clearedValues: FilterValues = {};
    filters.forEach(f => {
      clearedValues[f.id] = f.type === 'multi-select' ? [] : 'all';
    });
    onChange(clearedValues);
  }, [filters, onChange]);

  const isFiltered = filteredItems !== undefined && totalItems !== undefined && filteredItems !== totalItems;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Filter Icon & Label */}
        <div className="flex items-center gap-2 text-gray-500 mr-2">
          <SlidersHorizontal size={18} />
          <span className="text-sm font-medium hidden sm:inline">Filters</span>
        </div>

        {/* Filter Chips */}
        {filters.map((filter) => (
          <FilterChip
            key={filter.id}
            config={filter}
            value={values[filter.id]}
            onChange={(val) => handleFilterChange(filter.id, val)}
            isOpen={openFilter === filter.id}
            onToggle={() => setOpenFilter(openFilter === filter.id ? null : filter.id)}
            onClose={() => setOpenFilter(null)}
          />
        ))}

        {/* Extra inline filters (e.g. date range) */}
        {extraFilters}

        {/* Active Filters Count & Clear */}
        <AnimatePresence>
          {activeFiltersCount > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="flex items-center gap-2 ml-2"
            >
              <span className="
                px-2 py-1 rounded-lg
                bg-primary-500 text-white
                text-xs font-bold
              ">
                {activeFiltersCount} active
              </span>
              
              <motion.button
                onClick={onClearAll ?? handleClearAll}
                className="
                  flex items-center gap-1
                  px-2 py-1 rounded-lg
                  text-xs font-medium
                  text-gray-500 hover:text-gray-700
                  hover:bg-gray-100
                  transition-colors
                "
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <RotateCcw size={12} />
                Clear all
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Results Summary */}
      <AnimatePresence>
        {isFiltered && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <p className="text-sm text-gray-500">
              Showing{' '}
              <span className="font-semibold text-primary-500">{filteredItems}</span>
              {' '}of{' '}
              <span className="font-semibold text-gray-700">{totalItems}</span>
              {' '}results
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FilterBar;


